import { Formik, Form } from "formik";
import TextField from "../components/common/TextField";
import Button from "../components/common/Button";
import { ReactNode, useMemo } from "react";
import { useFetchAllCitiesQuery } from "../store";
import { City, TransportType, TransportTypeLabels } from "../types";
import { FaUser, FaMapMarkerAlt, FaMotorcycle } from "react-icons/fa";

interface RiderFormProps {
  initialValues: any;
  validationSchema: any;
  onSubmit: (
    values: any,
    formikHelpers: { resetForm: () => void }
  ) => Promise<void>;
  isLoading?: boolean;
}

const RiderForm: React.FC<RiderFormProps> = ({
  initialValues,
  validationSchema,
  onSubmit,
  isLoading,
}) => {
  const { data: cities } = useFetchAllCitiesQuery();

  const availableCities = useMemo(() => {
    if (!cities) return [];
    return cities.map((city: City) => ({
      value: city._id,
      label: city.displayName,
    }));
  }, [cities]);

  return (
    <div className="w-full max-w-5xl mx-auto">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
          setFieldValue,
        }) => (
          <Form className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Identity Column */}
            <div className="bg-white shadow rounded-lg p-6 flex flex-col gap-6 h-fit">
              <div className="flex items-center gap-3 mb-2 pb-4 border-b border-gray-100">
                <div className="p-2 bg-blue-50 text-primary rounded-lg">
                  <FaUser className="text-xl" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">
                    Identité & Connexion
                  </h2>
                  <p className="text-sm text-gray-500">
                    Informations personnelles
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="firstName"
                      className="text-sm font-medium text-gray-700"
                    >
                      Prénom
                    </label>
                    <TextField
                      id="firstName"
                      value={values.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      placeholder="Ex: Jean"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="lastName"
                      className="text-sm font-medium text-gray-700"
                    >
                      Nom
                    </label>
                    <TextField
                      id="lastName"
                      value={values.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      placeholder="Ex: Dupont"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="email"
                    className="text-sm font-medium text-gray-700"
                  >
                    Email
                  </label>
                  <TextField
                    id="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errors={errors}
                    touched={touched}
                    placeholder="Ex: jean.dupont@email.com"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="phone"
                    className="text-sm font-medium text-gray-700"
                  >
                    Téléphone
                  </label>
                  <TextField
                    id="phone"
                    value={values.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errors={errors}
                    touched={touched}
                    placeholder="Ex: 06 12 34 56 78"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="password"
                    className="text-sm font-medium text-gray-700"
                  >
                    Mot de passe
                  </label>
                  <TextField
                    id="password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errors={errors}
                    touched={touched}
                    placeholder="Minimum 8 caractères"
                    hideable
                  />
                </div>
              </div>
            </div>

            {/* Right Column: Address and Assignment */}
            <div className="flex flex-col gap-8">
              {/* Address Section */}
              <div className="bg-white shadow rounded-lg p-6 flex flex-col gap-6">
                <div className="flex items-center gap-3 mb-2 pb-4 border-b border-gray-100">
                  <div className="p-2 bg-blue-50 text-primary rounded-lg">
                    <FaMapMarkerAlt className="text-xl" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-800">
                      Adresse
                    </h2>
                    <p className="text-sm text-gray-500">
                      Coordonnées postales
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="address.street"
                      className="text-sm font-medium text-gray-700"
                    >
                      Rue
                    </label>
                    <TextField
                      id="address.street"
                      value={values.address.street}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      placeholder="Ex: 12 Rue de la Paix"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                      <label
                        htmlFor="address.zipCode"
                        className="text-sm font-medium text-gray-700"
                      >
                        Code postal
                      </label>
                      <TextField
                        id="address.zipCode"
                        value={values.address.zipCode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        placeholder="Ex: 75001"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label
                        htmlFor="address.city"
                        className="text-sm font-medium text-gray-700"
                      >
                        Ville
                      </label>
                      <TextField
                        id="address.city"
                        value={values.address.city}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        placeholder="Ex: Paris"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Assignment Section */}
              <div className="bg-white shadow rounded-lg p-6 flex flex-col gap-6">
                <div className="flex items-center gap-3 mb-2 pb-4 border-b border-gray-100">
                  <div className="p-2 bg-blue-50 text-primary rounded-lg">
                    <FaMotorcycle className="text-xl" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-800">
                    Affectation & Véhicule
                  </h2>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="city"
                      className="text-sm font-medium text-gray-700"
                    >
                      Ville d&apos;opération (Zone)
                    </label>
                    <select
                      id="city"
                      value={values.city}
                      onChange={(e) => setFieldValue("city", e.target.value)}
                      onBlur={handleBlur}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-shadow bg-white h-[42px]"
                    >
                      <option
                        value=""
                        disabled
                      >
                        Sélectionner une ville
                      </option>
                      {availableCities.map((city: any) => (
                        <option
                          key={city.value}
                          value={city.value}
                        >
                          {city.label}
                        </option>
                      ))}
                    </select>
                    {errors.city && touched.city && (
                      <div className="mt-1 text-xs text-red-500">
                        {errors.city as ReactNode}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="transportType"
                      className="text-sm font-medium text-gray-700"
                    >
                      Type de véhicule
                    </label>
                    <select
                      id="transportType"
                      value={values.transportType}
                      onChange={(e) =>
                        setFieldValue("transportType", e.target.value)
                      }
                      onBlur={handleBlur}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-shadow bg-white h-[42px]"
                    >
                      <option
                        value=""
                        disabled
                      >
                        Sélectionner un véhicule
                      </option>
                      {Object.values(TransportType).map((type) => (
                        <option
                          key={type}
                          value={type}
                        >
                          {TransportTypeLabels[type]}
                        </option>
                      ))}
                    </select>
                    {errors.transportType && touched.transportType && (
                      <div className="mt-1 text-xs text-red-500">
                        {errors.transportType as ReactNode}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-auto pt-6">
                <Button
                  className="w-full bg-primary text-white font-medium py-3 px-8 rounded-lg hover:bg-opacity-90 transition-all shadow-sm flex items-center justify-center gap-2 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting || isLoading}
                  type="submit"
                >
                  {isLoading ? "Enregistrement..." : "Enregistrer le livreur"}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default RiderForm;
